import { ErrorMessage, Field, FieldArray } from "formik";
import TextError from "../../forms/TextError";
import { Plus, Trash2, Copy } from "lucide-react";
import SelectItem from "../Admin/Common/SelectItem";
import SelectMultipleWithSearch from "../Admin/Common/SelectMultipleWithSearch";
import { AIR_CHARGE_UNIT_TYPE } from "../../utils/constant";
import { handleKeyPress } from "../../functions/Utils";
import { memo } from "react";
import { money, rowTotal } from "./chargeUtils";
import CopyFromMenu from "./CopyFromMenu";

// Same CSS Grid approach as the rest of ManualQoute — Ant Design's Select
// ignores flex-parent width, but honours a real grid cell width.
const ROW_GRID = {
  display: "grid",
  gridTemplateColumns: "1fr 120px 120px 120px 130px 70px",
  gap: 10,
  alignItems: "center",
};

const INPUT_CLS =
  "h-[36px] px-3 focus:outline-none block w-full text-sm border border-gray-200 rounded-lg text-gray-800 bg-white focus:border-yellow-400";
const READONLY_CLS =
  "h-[36px] px-3 flex items-center text-sm border border-gray-100 rounded-lg text-gray-500 bg-gray-50";
const LABEL_CLS =
  "text-[11px] font-medium text-gray-400 uppercase tracking-wide";

const iconBtnStyle = (danger) => ({
  width: 30,
  height: 30,
  borderRadius: 8,
  background: danger ? "#fef2f2" : "#f3f4f6",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: danger ? "#fca5a5" : "#9ca3af",
  flexShrink: 0,
  transition: "background 0.15s ease, color 0.15s ease",
});

const iconBtnHover = (e, danger, entering) => {
  e.currentTarget.style.background = entering
    ? danger
      ? "#fee2e2"
      : "#ebedf0"
    : danger
      ? "#fef2f2"
      : "#f3f4f6";
};

const ChargeLineItemsEditor = memo(
  ({
    formikData,
    basePath,
    sectionKey,
    items,
    chargeList,
    title,
    totalWeight,
    minItems = 1,
    copySources,
  }) => {
    const itemsPath = `${basePath}.items`;
    const total = (items || []).reduce(
      (sum, item) => sum + rowTotal(item, totalWeight),
      0
    );

    return (
      <div
        style={{
          border: "1px solid #ece6d8",
          borderRadius: 12,
          padding: "14px 16px",
          background: "#fff",
          boxShadow: "0 1px 2px rgba(16,24,40,0.03)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <span style={{ fontWeight: 600, color: "#374151", fontSize: 13 }}>
            {title}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <CopyFromMenu
              sources={copySources}
              onCopy={(source) =>
                formikData.setFieldValue(
                  itemsPath,
                  JSON.parse(
                    JSON.stringify(source.option?.charges?.[sectionKey]?.items || [])
                  )
                )
              }
            />
            <span
              style={{
                fontSize: 12,
                color: "#8c6a12",
                background: "#fff8e7",
                border: "1px solid #f2e2b8",
                borderRadius: 999,
                padding: "3px 12px",
              }}
            >
              Subtotal <strong style={{ color: "#1f2937" }}>{money(total)}</strong>
            </span>
          </div>
        </div>

        <FieldArray name={itemsPath}>
          {({ remove, push, insert }) => (
            <div>
              {items?.length > 0 && (
                <div style={{ ...ROW_GRID, marginBottom: 6 }}>
                  {["Charge", "Unit", "Rate", "Chg. weight", "Total", ""].map(
                    (lbl, i) => (
                      <span key={i} className={LABEL_CLS}>
                        {lbl}
                      </span>
                    )
                  )}
                </div>
              )}

              {items?.map((item, index) => {
                const perKg = item?.unit === "perkg";
                return (
                  <div key={index} style={{ ...ROW_GRID, marginBottom: 8 }}>
                    <div>
                      <Field
                        as={SelectMultipleWithSearch}
                        options={chargeList}
                        width="100%"
                        id={`${itemsPath}.${index}.charge`}
                        name={`${itemsPath}.${index}.charge`}
                        placeholder="Select charge"
                        handleChange={formikData.handleChange}
                        setFieldValue={formikData.setFieldValue}
                      />
                      <ErrorMessage
                        name={`${itemsPath}.${index}.charge`}
                        component={TextError}
                      />
                    </div>

                    <div>
                      <Field
                        as={SelectItem}
                        options={AIR_CHARGE_UNIT_TYPE}
                        width="100%"
                        id={`${itemsPath}.${index}.unit`}
                        name={`${itemsPath}.${index}.unit`}
                        placeholder="Unit"
                        handleChange={formikData.handleChange}
                        setFieldValue={(_event, value) =>
                          formikData.setFieldValue(
                            `${itemsPath}.${index}.unit`,
                            value
                          )
                        }
                      />
                    </div>

                    <div>
                      <Field
                        type="text"
                        id={`${itemsPath}.${index}.unitRate`}
                        name={`${itemsPath}.${index}.unitRate`}
                        placeholder="0.00"
                        className={INPUT_CLS}
                        onChange={formikData.handleChange}
                        onKeyPress={handleKeyPress}
                      />
                      <ErrorMessage
                        name={`${itemsPath}.${index}.unitRate`}
                        component={TextError}
                      />
                    </div>

                    <div className={READONLY_CLS}>
                      {perKg ? `${totalWeight ?? 0} kg` : "—"}
                    </div>

                    <div
                      className={READONLY_CLS}
                      style={{
                        fontWeight: 600,
                        color: "#1f2937",
                        justifyContent: "flex-end",
                      }}
                    >
                      {money(rowTotal(item, totalWeight))}
                    </div>

                    <div style={{ display: "flex", gap: 4 }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          insert(index, { ...item });
                        }}
                        style={iconBtnStyle(false)}
                        onMouseEnter={(e) => iconBtnHover(e, false, true)}
                        onMouseLeave={(e) => iconBtnHover(e, false, false)}
                        title="Duplicate"
                      >
                        <Copy size={13} />
                      </button>
                      {items.length > minItems && (
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          style={iconBtnStyle(true)}
                          onMouseEnter={(e) => iconBtnHover(e, true, true)}
                          onMouseLeave={(e) => iconBtnHover(e, true, false)}
                          title="Remove"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              <button
                type="button"
                onClick={() => push({ charge: "", unit: "perkg", unitRate: "" })}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#FCB614",
                  background: "none",
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                  padding: "6px 8px",
                  marginTop: 2,
                  transition: "background 0.15s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#fffbf0")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
              >
                <Plus size={15} />
                Add charge
              </button>
            </div>
          )}
        </FieldArray>
      </div>
    );
  }
);

export default ChargeLineItemsEditor;
